import React, { useState, useEffect, useContext } from 'react';
import { TextField, Button, Typography } from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import YesNoDialog from '../YesNoDialog';
import { useParams } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import { DateTime } from 'luxon';
import '../App.css';
import EventStatus from './EventStatus';
import { AuthContext } from '../AuthContextProvider';

const VideoConferenceEvent = () => {
  const [meetingId, setMeetingId] = useState('');
  const [meetingPassword, setMeetingPassword] = useState('');
  const [scheduledEvent, setScheduledEvent] = useState(null);
  const [eventUsers, setEventUsers] = useState([]);
  const [status, setStatus]= useState(scheduledEvent ? EventStatus.getEventStatus(scheduledEvent) : Constants.EVENT_NOT_READY);
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openConfirmCredsDialog, setOpenConfirmCredsDialog] = useState(false);
  const { user } = useContext(AuthContext);
  
  const { id } = useParams();

  useEffect(() => {
    const fetchEvent = async (id) => {
      const params = {
        _id: id,
      };
      await rpc({
        method: 'GetScheduledEvent',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setScheduledEvent
      });
    };
    fetchEvent(id);
  }, [id]);

  useEffect(() => {
    const onFetchSuccess = (usersInGroup) => {
      setEventUsers((prevUsers) => {
        const allUsers = prevUsers.concat(usersInGroup);
        const uniqueIds = new Set();
        const result = allUsers.filter((user) => {
          const isDuplicate = uniqueIds.has(user._id);
          uniqueIds.add(user._id);
          return !isDuplicate;
        });
        return result;
      });
    };
  
    const fetchUsersInGroup = async (group_id) => {
      const params = {
        _eventGroups: group_id,
      };
      rpc({
        method: 'GetUsers',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };
    if (scheduledEvent !== null) {
      setStatus(EventStatus.getEventStatus(scheduledEvent));
      scheduledEvent._eventGroups.forEach(group => {
        fetchUsersInGroup(group);
      });
    }
  }, [scheduledEvent]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (scheduledEvent !== null) {
        setStatus(EventStatus.getEventStatus(scheduledEvent));
      }
    }, 5000); // Refresh every 5 seconds    
    return () => {
      // Clean up the timer when the component unmounts
      clearInterval(timer);
    };
  }, [scheduledEvent]);

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const launchZoom = (meetingId, password, caller) => {
    const encodedCaller = encodeURIComponent(caller);
    const zoomUrl = `zoomus://zoom.us/start?confno=${meetingId}&pwd=${password}&uname=${encodedCaller}`;
    const fallbackUrl = `https://zoom.us/j/${meetingId}?pwd=${password}&uname=${encodedCaller}`;

    // Try deep link
    window.open(zoomUrl, '_blank');

    // Fallback to web Zoom after short delay
    setTimeout(() => {
      window.open(fallbackUrl, '_blank');
    }, 3000);
  };

  const startVideoConference = async () => {
    setOpenConfirmCredsDialog(false);
    if (meetingId === '' || meetingPassword === '') {
      setErrorMessage('Please enter a meeting id and password.');
      setOpenErrorDialog(true);
      return;
    }
    EventStatus.setEventStatus(scheduledEvent, Constants.EVENT_STARTED);
    setStatus(Constants.EVENT_STARTED);
    const id = meetingId.replace(/\D/g, '');
    const params = {
      _id: scheduledEvent._id,
      status: Constants.EVENT_STARTED,
      zoomId: id,
      zoomPassword: meetingPassword
    };
    rpc({
      method: 'UpdateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: () => {
        const caller = `${user.first} ${user.last}`;
        launchZoom(id, meetingPassword, caller);
      }
    });
  };

  const getTitle = () => {
    if (scheduledEvent === null) {
      return '';
    }
    const shownOn = DateTime.fromISO(scheduledEvent.shownOn)
      .setZone(user._site.timezone)
      .toFormat(Constants.TIME_FORMAT_LUXON);
    const statusText= Constants.EVENT_STATUS_TEXT[status];
    return`${scheduledEvent._event.name} ${shownOn} ${statusText}`;
  };

  const getUsers = () => {
    return eventUsers
      .filter((user) => user.useTwilio === true)
      .map((user) => `${user.first} ${user.last}`)
      .join(', ');
  };

  const canStart = () => {
    return status === Constants.EVENT_STOPPED && meetingId !== '' && meetingPassword !== '';
  };

  return (
    <div className="VideoConferenceEventDetails">
      <Typography variant="h6" gutterBottom>
        Event {getTitle()}
      </Typography>
      <Typography>
        Make sure that Zoom is installed prior to pressing START.<br/>
        If Zoom fails to launch (iPad or iPhone) then be sure to manually start the Zoom meeting.<br/><br/>
        Users: {getUsers()}
      </Typography>
      <TextField className="TextField"
        label="Meeting Id"
        value={meetingId}
        onChange={(e) => setMeetingId(e.target.value)}
      />
      <TextField className="TextField"
        label="Password"
        value={meetingPassword}
        onChange={(e) => setMeetingPassword(e.target.value)}
      />
      <Button className="Button" variant="contained" color="primary" 
        disabled={!canStart()}
        onClick={() => {setOpenConfirmCredsDialog(true);}}>
        Start
      </Button>
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
      <YesNoDialog open={openConfirmCredsDialog} 
        onErrorDialogClose={() => {setOpenConfirmCredsDialog(false);}}
        onYes={startVideoConference} 
        title="Verify Meeting Credentials"
        message={'WARNING: Double check the id and password because sending the wrong information cannot be corrected. ' +
          `Are the meeting id (${meetingId}) and password (${meetingPassword}) correct?`}
      />
    </div>
  );
};

export default VideoConferenceEvent;
